import { memo, useState, useEffect, useCallback } from 'react';
import {
  Drawer,
  IconButton,
  Box,
  Typography,
  Stack,
  Chip,
  Divider,
  Grid,
  Avatar,
  CircularProgress,
  Button,
} from '@mui/material';
import {
  Close,
  LocationOn,
  Inventory2,
  LocalShipping,
  Person,
  Phone,
  Payment,
  Scale,
  AccessTime,
} from '@mui/icons-material';
import { themeColors } from '../theme/theme';
import ParcelJourneyTimeline from './ParcelJourneyTimeline';
import useApiCall from '../services/api/useApiCall';
import { SOCKET_EVENTS } from '@/hooks/socket';
import { buildApiConfig } from '../services/api/apiClient';
import { endPointEnums } from '../services/api/endpoints';
import { useTranslation } from '@/kit/useTranslation';

const BookingDetailsSheet = memo(({ booking, onClose, onRefresh }) => {
  const [legs, setLegs] = useState([]);
  const [loadingLegs, setLoadingLegs] = useState(false);
  const { makeApiCall } = useApiCall();
  const { t } = useTranslation();

  // Fetch legs for multi-stage parcels
  useEffect(() => {
    const fetchLegs = async () => {
      if (!booking || !booking.id) {
        return;
      }

      setLoadingLegs(true);
      try {
        const config = buildApiConfig(endPointEnums.GET_LEGS_BY_PARCEL, {
          parcelId: booking.id,
        });
        const response = await makeApiCall(config);

        if (response?.data) {
          setLegs(response.data);
        }
      } catch (error) {
        // Error handled silently
      } finally {
        setLoadingLegs(false);
      }
    };

    fetchLegs();
  }, [booking, makeApiCall]);

  // Listen for parcel updates via socket events
  const handleParcelUpdate = useCallback(
    (event) => {
      const { parcelId } = event.detail || {};

      if (parcelId && booking?.id && parcelId === booking.id) {
        // Refetch legs
        const fetchLegs = async () => {
          try {
            const config = buildApiConfig(endPointEnums.GET_LEGS_BY_PARCEL, {
              parcelId: booking.id,
            });
            const response = await makeApiCall(config);
            if (response?.data) {
              setLegs(response.data);
            }
          } catch (error) {
            // Error handled silently
          }
        };
        fetchLegs();

        // Call parent refresh if provided
        if (onRefresh) {
          onRefresh();
        }
      }
    },
    [booking?.id, makeApiCall, onRefresh]
  );

  useWindowEvent(SOCKET_EVENTS.PARCEL_UPDATED, handleParcelUpdate);

  if (!booking) return null;

  // Status color mapping
  const getStatusColor = (status) => {
    const statusLower = status?.toLowerCase() || '';
    if (statusLower.includes('transit')) return '#000000';
    if (statusLower.includes('cancelled')) return '#f44336';
    if (statusLower.includes('delivered')) return '#000000';
    if (statusLower.includes('pending')) return '#2d2d2d';
    return themeColors.primaryColor;
  };

  const statusColor = getStatusColor(booking.status);

  return (
    <Drawer
      anchor="bottom"
      open={!!booking}
      onClose={onClose}
      PaperProps={{
        sx: {
          borderTopLeftRadius: 24,
          borderTopRightRadius: 24,
          maxHeight: '90vh',
          // allow larger width on desktop so timeline has more room
          maxWidth: { xs: '100%', sm: 720 },
          width: '100%',
          mx: 'auto',
          overflowX: 'hidden',
        },
      }}
    >
      {/* Drag Handle */}
      <Box
        sx={{
          width: 40,
          height: 5,
          bgcolor: '#e5e7eb',
          borderRadius: 999,
          mx: 'auto',
          mt: 1.5,
          mb: 1,
        }}
      />

      {/* Header */}
      <Box
        sx={{
          px: 3,
          py: 2,
          background: `linear-gradient(135deg, ${statusColor}15 0%, ${statusColor}05 100%)`,
          borderBottom: `2px solid ${statusColor}30`,
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
          <Typography variant="h6" fontWeight={700}>
            {t('booking.bookingDetails')}
          </Typography>
          <IconButton onClick={onClose} size="small" aria-label={t('booking.closeBookingDetails')}>
            <Close />
          </IconButton>
        </Stack>

        <Stack direction="row" alignItems="center" spacing={2}>
          <Avatar
            sx={{
              bgcolor: `${statusColor}20`,
              color: statusColor,
              width: 48,
              height: 48,
            }}
          >
            <Inventory2 />
          </Avatar>
          <Box>
            <Typography variant="h6" fontWeight={700}>
              {t('booking.bookingDetails')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              ID: {booking.id}
            </Typography>
          </Box>
        </Stack>
      </Box>

      {/* Main Content */}
      <Box
        sx={{
          px: 2,
          py: 2,
          overflowY: 'auto',
          maxHeight: 'calc(90vh - 120px)',
        }}
      >
        <Stack spacing={3}>
          {/* Status */}
          <Box>
            <Chip
              label={booking.status}
              sx={{
                bgcolor: `${statusColor}15`,
                color: statusColor,
                fontWeight: 700,
                fontSize: 14,
                px: 1,
                mt: 2,
              }}
            />
            <Typography variant="caption" color="text.secondary" display="block" mt={0.5} ml={1}>
              {booking.dateTime}
            </Typography>
          </Box>

          <Divider />

          {/* Route Information */}
          <Box>
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
              {t('booking.routeInformation')}
            </Typography>
            <Stack spacing={2}>
              {/* From */}
              <Stack direction="row" spacing={2}>
                <Avatar
                  sx={{
                    bgcolor: '#00000020',
                    color: '#000000',
                    width: 40,
                    height: 40,
                  }}
                >
                  <LocationOn />
                </Avatar>
                <Box flex={1}>
                  <Typography variant="caption" color="text.secondary">
                    {t('booking.from')}
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {booking.from?.label}
                  </Typography>
                </Box>
              </Stack>

              {/* To */}
              <Stack direction="row" spacing={2}>
                <Avatar
                  sx={{
                    bgcolor: '#f4433620',
                    color: '#f44336',
                    width: 40,
                    height: 40,
                  }}
                >
                  <LocationOn />
                </Avatar>
                <Box flex={1}>
                  <Typography variant="caption" color="text.secondary">
                    {t('booking.to')}
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {booking.to?.label || booking.to?.area || booking.to?.city || 'N/A'}
                  </Typography>
                </Box>
              </Stack>
            </Stack>
          </Box>

          <Divider />

          {/* Parcel Details */}
          <Box>
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
              {t('booking.parcelDetailsLabel')}
            </Typography>
            <Grid container spacing={1} alignItems="center">
              <Grid item xs={6} sm={6} md={6}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: '50%',
                      bgcolor: '#f1f5f9',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <LocalShipping sx={{ fontSize: 18, color: themeColors.primaryColor }} />
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {t('booking.type')}
                    </Typography>
                    <Typography variant="body2" fontWeight={700}>
                      {booking.itemType || 'Parcel'}
                    </Typography>
                  </Box>
                </Stack>
              </Grid>
              <Grid item xs={6} sm={6} md={6}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: '50%',
                      bgcolor: '#fff7ed',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Scale sx={{ fontSize: 18, color: '#2d2d2d' }} />
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {t('booking.weight')}
                    </Typography>
                    <Typography variant="body2" fontWeight={700}>
                      {booking.weight ? `${booking.weight} kg` : booking.size || 'N/A'}
                    </Typography>
                  </Box>
                </Stack>
              </Grid>
              {booking.parcelVolumeCbm && (
                <Grid item xs={6} sm={6} md={6}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: '50%',
                        bgcolor: '#e3f2fd',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Inventory2 sx={{ fontSize: 18, color: '#000000' }} />
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        {t('booking.volume')}
                      </Typography>
                      <Typography variant="body2" fontWeight={700}>
                        {booking.parcelVolumeCbm} m³
                      </Typography>
                    </Box>
                  </Stack>
                </Grid>
              )}
            </Grid>
          </Box>

          {/* Receiver Details */}
          {booking.receiverName && (
            <>
              <Divider />
              <Box>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                  {t('booking.receiverDetails')}
                </Typography>
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Person sx={{ fontSize: 18, color: 'action.active' }} />
                    <Typography variant="body2">{booking.receiverName}</Typography>
                  </Stack>
                  {booking.receiverPhone && (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Phone sx={{ fontSize: 18, color: 'action.active' }} />
                      <Typography variant="body2">{booking.receiverPhone}</Typography>
                    </Stack>
                  )}
                </Stack>
              </Box>
            </>
          )}

          {/* Payment Information */}
          {booking.price && (
            <>
              <Divider />
              <Box>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                  {t('booking.paymentInformation')}
                </Typography>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Payment sx={{ fontSize: 18, color: 'action.active' }} />
                    <Typography variant="body2" color="text.secondary">
                      {t('booking.totalAmount')}
                    </Typography>
                  </Stack>
                  <Typography variant="h6" fontWeight={700} color={themeColors.primaryColor}>
                    ₹{booking.price}
                  </Typography>
                </Stack>
              </Box>
            </>
          )}

          {/* Journey Timeline for multi-stage deliveries */}
          {(legs.length > 0 || loadingLegs) && (
            <>
              <Divider />
              <Box>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                  {t('booking.journeyTimeline')}
                </Typography>
                {loadingLegs ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : (
                  <ParcelJourneyTimeline legs={legs} parcel={booking} />
                )}
              </Box>
            </>
          )}
        </Stack>
      </Box>
    </Drawer>
  );
});

BookingDetailsSheet.displayName = 'BookingDetailsSheet';

export default BookingDetailsSheet;
